"use client";

import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import InputQuantity from "../commons/InputQuantity";
import { useEffect, useState } from "react";
import DialogConfirmation from "./DialogConfirmation";
import { createOffer } from "../../firebase/offers";
import { getListingByID } from "../../firebase/listings"
import { useAtom } from "jotai";
import { recipientIdAtom, showIndividualMessageBoxAtom, userAtom } from "../../store";
import { formatDate } from '../../utils'
import { boxTypes, conditions, rarities } from "../../utils/data";
import { getUserData } from "../../firebase/users";
import { Link } from "react-router-dom";
import DialogReview from "./DialogReview";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
  Font
} from "@react-pdf/renderer";

/**
 * Convert ArrayBuffer -> base64 safely (chunked to avoid call stack/arg limits).
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000; // 32KB chunks
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

/**
 * Register OTF fonts for use in @react-pdf/renderer (browser).
 * - family: the font family name you'll use in styles
 * - regularUrl: URL (relative or absolute) to Regular OTF
 * - boldUrl: URL to Bold OTF (optional)
 */
async function registerFontsBrowser({
  family = "CustomFont",
  regularUrl,
  boldUrl
} = {}) {
  if (!regularUrl) throw new Error("regularUrl is required");

  // fetch in parallel (if boldUrl provided)
  const fetches = [fetch(regularUrl).then(r => {
    if (!r.ok) throw new Error(`Failed to load font ${regularUrl}: ${r.status}`);
    return r.arrayBuffer();
  })];

  if (boldUrl) {
    fetches.push(fetch(boldUrl).then(r => {
      if (!r.ok) throw new Error(`Failed to load font ${boldUrl}: ${r.status}`);
      return r.arrayBuffer();
    }));
  }

  const buffers = await Promise.all(fetches);
  const regularBase64 = arrayBufferToBase64(buffers[0]);
  const regularDataUrl = `data:font/otf;base64,${regularBase64}`;

  const fonts = [{ src: regularDataUrl, fontWeight: "normal" }];

  if (boldUrl && buffers[1]) {
    const boldBase64 = arrayBufferToBase64(buffers[1]);
    const boldDataUrl = `data:font/otf;base64,${boldBase64}`;
    fonts.push({ src: boldDataUrl, fontWeight: "bold" });
  }

  // Register the family with @react-pdf/renderer
  Font.register({ family, fonts });
}

await registerFontsBrowser({
  family: "InterCustom",
  regularUrl: "/assets/fonts/Aeonik-Black.otf",
  boldUrl: "/assets/fonts/Aeonik-Bold.otf"
});


const styles = StyleSheet.create({
  page: {
    padding: 0,
    fontSize: 12,
    position: "relative",
    fontFamily: "Helvetica"
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
    backgroundColor: "#181b1c",
    paddingRight: 24,
    paddingLeft: 24
  },
  logo: {
    width: 60,
    height: 60,
    objectFit: "contain"
  },
  sellerBuyer: {
    marginBottom: 8,
    lineHeight: 1.6,
    paddingLeft: 24,
    paddingRight: 24
  },
  title: {
    fontSize: 24,
    fontFamily: "InterCustom",
    textAlign: "center",
    fontWeight: "bold",
    marginVertical: 12
  },
  info: {
    marginBottom: 8,
    lineHeight: 1.6,
    paddingLeft: 24,
    paddingRight: 24
  },
  footer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: 24,
    textAlign: "center",
    fontSize: 10,
    color: "#666",
    backgroundColor: "#181b1c"
  },
  splitTop: {
    marginTop: 12,
  },
  splitBottom: {
    marginBottom: 12,
  },
  orderNumber: {
    fontFamily: "InterCustom",
    color: "white"
  }
});

const formatSaleNumber = (num) => {
    const n = Number.isFinite(Number(num)) ? Number(num) : 0;
    return `#${String(n).padStart(6, "0")}`;
};

const MyDoc = ({ data, logo }) => {
  // safe formatting for price in case it's a string/number
  const priceFormatted =
    typeof data.price === "number"
      ? data.price.toFixed(2)
      : Number(data.price || 0).toFixed(2);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header (logo on the right) */}
        <View style={styles.header}>
          {/* left side can be used for company name/address if needed */}
          <View>
            <Text style={styles.orderNumber}>{formatSaleNumber(data.number)}</Text>
          </View>

          {/* Logo (top-right) */}
          {logo ? <Image src={logo} style={styles.logo} /> : null}
        </View>

        {/* Centered, larger, bold Receipt title */}
        <Text style={styles.title}>RECEIPT</Text>
        
        {/* Seller / Buyer at top */}
        <View style={styles.sellerBuyer}>
          <Text>Seller: </Text>
          <Text>{data.seller}</Text>
          <Text>{data.sellerInfo.address.street}</Text>
          <Text>{data.sellerInfo.address.city}, {data.sellerInfo.address.state} {data.sellerInfo.address.postalCode}</Text>
          <Text>{data.sellerInfo.address.country}</Text>
          <Text style={styles.splitTop}>Buyer: </Text>
          <Text>{data.buyer}</Text>
          <Text>{data.buyerInfo.address.street}</Text>
          <Text>{data.buyerInfo.address.city}, {data.buyerInfo.address.state} {data.buyerInfo.address.postalCode}</Text>
          <Text style={styles.splitBottom}>{data.buyerInfo.address.country}</Text>
        </View>

        {/* Main information, with increased spacing */}
        <View>
          <Text style={styles.info}>Product Name: {data.name}</Text>
          <Text style={styles.info}>Quantity: {data.quantity}</Text>
          <Text style={styles.info}>Price per Unit: ${priceFormatted}</Text>
          <Text style={styles.info}>Date: {data.date}</Text>
        </View>

        {/* Footer fixed near the bottom */}
        <Text style={styles.footer} fixed>
          www.cardshopexchange.com
        </Text>
      </Page>
    </Document>
  );
};

export default function DialogReceipt({
    open,
    onClose,
    item,
    card,
    type
}) {
    // const [showConfirmModal, setShowConfirmModal] = useState(false)
    // const [card, setCard] = useState(null)
    const [party, setParty] = useState(null)
    const [showIndividualMessageBox, setShowIndividualMessageBox] = useAtom(showIndividualMessageBoxAtom)
    const [recipientId, setRecipientId] = useAtom(recipientIdAtom)
    const [showReviewModal, setShowReviewModal] = useState(false)
    const [userData, setUserData] = useAtom(userAtom)


    const handleDownload = async () => {
        Promise.all([
            getUserData(item.receiver),
            getUserData(item.sender)
        ])
            .then(async ([receiverData, senderData]) => {
                const sampleData = {
                    name: card.title,
                    number: item?.number,
                    quantity: item.count,
                    price: item.price,
                    date: new Date(item.created_at.seconds * 1000).toLocaleDateString(),
                    seller: receiverData.displayName,
                    buyer: senderData.displayName,
                    sellerInfo: receiverData.shippingInfo,
                    buyerInfo: senderData.shippingInfo,
                };

                console.log(receiverData)

                try {
                    const blob = await pdf(<MyDoc data={sampleData} logo={'/assets/logos/logo.png'} />).toBlob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `Receipt-${item.id}.pdf`;
                    document.body.appendChild(a); // ensure it's in DOM for some browsers
                    a.click();
                    a.remove();
                    // revoke after a small delay to ensure download started
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                } catch (err) {
                    console.error("PDF generation failed:", err);
                }
            })
            .catch(err => {
                console.error("Failed to fetch user data:", err);
            });
    };

    useEffect(() => {
        // getListingByID(item.cardId).then((data) => {
        //     setCard(data)
        // })
        console.log("card", card)
        console.log("items", item)
        if (type === 'purchased') {
            getUserData(item.receiver).then((data) => {
                setParty(data)
            })
        } else {
            getUserData(item.sender).then((data) => {
                setParty(data)
            })
        }
    }, [])

    const getCondition = (value) => {
        const condition = conditions.filter(it => it.value === value)[0];
        return condition ? condition.label : 'Unknown';
    }

    const getRarity = (value) => {
        const rarity = boxTypes.filter(it => it.value === value)[0];
        return rarity ? rarity.label : 'Unknown';
    }

    const getBoxType = (value) => {
        const boxType = boxTypes.filter(it => it.value === value)[0];
        return boxType ? boxType.label : 'Unknown';
    }

    const setMessageToParty = () => {
        setShowIndividualMessageBox(true)
        setRecipientId(party.id)
    }

    return (
        <>
            <DialogReview
                open={showReviewModal}
                onClose={() => setShowReviewModal(false)}
                onCloseReceipt={onClose}
                item={item}
                reviewer={userData}
            />

            <Dialog open={open} onClose={onClose} className="relative z-[10000]">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-[#000] backdrop-blur-sm bg-opacity-75 transition-opacity"
                />

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center sm:p-0">
                        <DialogPanel
                            transition
                            className="relative transform overflow-hidden border-[1px] border-gray bg-[#141414] backdrop-blur-sm shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in w-full max-w-[644px]"
                        >
                            {card && <div>
                                <div className="w-full relative pt-9 pb-6 sm:px-6 px-2">
                                    <div className="text-white text-[24px]">
                                        PAID: ${((card.price || 0) * (item.count || 1)).toFixed(2)}
                                    </div>

                                    <button onClick={onClose} className="absolute right-4 top-4">
                                        <img src="/assets/icons/icon-close-white.svg" alt="close" />
                                    </button>

                                    <div className="border-[1px] border-gray mt-6 px-4 py-7 text-[#747474]">
                                        <div className="text-[20px] font-bold">
                                            {card.title}
                                        </div>

                                        <hr className="my-4 border-gray"></hr>

                                        <div className="grid grid-cols-5">
                                            {
                                                card.pictures && card.pictures.length > 0 ?
                                                    card.pictures.map(item =>
                                                        <img src={item} key={item} className="w-[90px] h-[90px] object-cover border-gray" />
                                                    ) :
                                                    <div className="text-gray-500 text-sm">No images available</div>
                                            }
                                        </div>

                                        <hr className="my-4 border-gray"></hr>

                                        <div className="text-sm">
                                            <div>
                                                Description:
                                            </div>

                                            <div>
                                                {card.description}
                                            </div>
                                        </div>

                                        <hr className="my-4 border-gray"></hr>

                                        <div className="flex w-full justify-between items-end">
                                            <div className="w-[200px]">
                                                <div className="text-xs w-full flex justify-between">
                                                    <span className="font-bold uppercase">Released Year: </span>
                                                    {card.released_year}
                                                </div>
                                                <div className="text-xs w-full flex justify-between mt-1">
                                                    <span className="font-bold uppercase">Condition: </span>
                                                    {card.condition ? getCondition(card.condition) : 'N/A'}
                                                </div>
                                                {/* <div className="text-xs w-full flex justify-between mt-1">
                                                    <span className="font-bold uppercase">Rarity: </span>
                                                    {getRarity(card.rarity)}
                                                </div> */}
                                                <div className="text-xs w-full flex justify-between mt-1">
                                                    <span className="font-bold uppercase">Box Type: </span>
                                                    {card.rarity ? getBoxType(card.rarity) : 'N/A'}
                                                </div>
                                                <div className="text-xs w-full flex justify-between mt-1">
                                                    <span className="font-bold uppercase">Quantity: </span>
                                                    {item.count || 'N/A'}
                                                </div>
                                                <div className="text-xs w-full flex justify-between mt-1">
                                                    <span className="font-bold uppercase">Date Posted: </span>
                                                    {card.created_at?.seconds ? formatDate(card.created_at.seconds) : 'N/A'}
                                                </div>
                                            </div>

                                            <button
                                                className="flex border-[1px] border-white text-sm sm:px-8 px-4 py-3 mt-3 bg-[#3B3B3B] text-white transition-all max-w-[200px]"
                                                onClick={handleDownload}
                                            >
                                                <img src="/assets/icons/download.svg" alt='message' className="mr-1" />

                                                Download PDF
                                            </button>
                                        </div>
                                    </div>

                                    {
                                        party &&
                                        <div className="border-[1px] border-gray mt-3 px-4 py-7">
                                            <div className="w-full flex items-center justify-between pb-3 border-b border-b-[#fff]/20">
                                                <span className="font-aero uppercase text-base lg:text-xl text-white">
                                                    {type === 'purchased' ? 'Seller' : 'Buyer'}
                                                </span>
                                                <div className="flex items-center space-x-2">
                                                    <div className="flex items-center">
                                                    </div>
                                                    {
                                                        item.review && party.ratings &&
                                                        <span className="flex items-center text-md text-white">
                                                            <div className="mr-2 flex">
                                                                {[1, 2, 3, 4, 5].map(item =>
                                                                    <div key={`user-rating-${item}`}>
                                                                        <img src="/assets/icons/icon-review-primary-star.svg" className="w-[20px]" alt="star" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {(party.ratings || 0).toFixed(1)}
                                                        </span>
                                                    }
                                                </div>
                                            </div>

                                            <div className="pt-5 flex items-start justify-between">
                                                <div className="flex items-center space-x-2 lg:space-x-4">
                                                    <div className="w-12 h-12 flex justify-center items-center">
                                                        <img src="/assets/avatars/default.png" className="w-full h-full" alt="seller" />
                                                    </div>

                                                    <div className="flex flex-col items-start">
                                                        <span className="text-sm lg:text-base text-white">
                                                            {party.displayName || 'Unknown User'}
                                                        </span>

                                                        {/* <Link className="text-[10px] lg:text-xs flex items-center text-primary underline-primary">
                                                            View Profile
                                                        </Link> */}
                                                    </div>
                                                </div>

                                                <div>
                                                    <button
                                                        className="flex border-[1px] border-primary text-sm sm:px-8 px-4 py-3 bg-[#24343C] text-white transition-all"
                                                        onClick={setMessageToParty}
                                                    >
                                                        <img src="/assets/icons/icon-message.svg" alt='message' className="mr-1" />

                                                        Send Message
                                                    </button>

                                                    {
                                                        type === 'purchased' ?
                                                            item.review ?
                                                                <button
                                                                    className="flex border-[1px] border-white text-sm sm:px-8 px-4 py-3 mt-3 bg-[#191919] opacity-60 text-white transition-all w-full"
                                                                    disabled
                                                                >
                                                                    <img src="/assets/icons/icon-star-white.svg" alt='message' className="mr-1" />

                                                                    Review Done
                                                                </button> :
                                                                <button
                                                                    className="flex border-[1px] border-white text-sm sm:px-8 px-4 py-3 mt-3 bg-[#3B3B3B] text-white transition-all w-full"
                                                                    onClick={() => setShowReviewModal(true)}
                                                                >
                                                                    <img src="/assets/icons/icon-star-white.svg" alt='message' className="mr-1" />

                                                                    Leave Review
                                                                </button>
                                                            : ''
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>}
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>
        </>
    );
}
